import { calculateTradePrice } from "@dmf/calculator-library";
import { useEffect, useMemo, useState } from "react";
import { createContainer } from "unstated-next";
import { TaxAdvert } from "../screens/forms/tax/advert/types";
import { EquipmentCatalog, NewPriceManager } from "../types/newPriceTypes";
import { TaxCase, TaxScenario, TaxScenarios } from "../types/taxTypes";
import { RegistrationFeeOutput } from "../screens/forms/tax/taxFlow/registrationFeeHelpers";
import TaxCaseStore from "./taxCaseStore";
import { regulateEquipmentList } from "../screens/forms/tax/taxFlow/util";
import moment from 'moment';
import { safeNum } from "../utilities/number";

type TradePriceOutput = ReturnType<typeof calculateTradePrice>
export type NewPriceType = "NEW_PRICE" | "TAXABLE_VALUE";
/**
 * Rename as needed:
 * - TaxFlow (preferred if you want the "process" semantics)
 * - TaxOffer (if that matches your domain language better)
 */
export type TaxFlow = {
  newPrice?: {
    newPriceWithoutEquipment?: number;
    equipmentPrice?: number;
    newPrice?: number;
    note?: string;
    newPriceType?: NewPriceType;
    taxableValueNewPrice?: null | {
      equipmentSum?: number | null,
      newPrice?: number | null,
      taxableValue?: number | null,
      registrationFee?: number | null
    },
    taxableValue?: number;
    taxableValueBreakdown?: {
      purchase?: {
        purchasePrice?: number | null;
        profitMargin?: number | null;
        vatValue?: number | null;
        taxableValue?: number | null;
      }
      sale?: {
        salesPrice?: number | null;
        registrationFee?: number | null;
        taxableValue?: number | null;
      };
    }
    manager?: NewPriceManager;
  }
  tradePrice?: {
    tradePrice?: number;
    output?: null | TradePriceOutput;
    regulatedEquipmentMap?: null | RegulatedEquipmentMap;
    summary?: {
      baseTradePrice?: number,
      additionalRegulation?: number,
      finalTradePrice?: number,
    }
    overrides?: {
      additionalRegulation?: {
        amount?: number;
        note?: string;
      }
      mileage?: number,
    }
  }
  adverts?: TaxAdvert[];
  registrationFee?: {
    registrationFee?: number | null;
    output?: null | RegistrationFeeOutput;
  }
  offer?: {
    collateral?: {
      total?: number | null;
      baseFee?: number | null;
      b2cFee?: number | null;
      b2cBreakdown?: null | {
        total?: number;
        standard?: number;
        nonElectric?: number;
        electric?: number;
      }
    }
    licensePlateFee?: number | null;
    specialCaseFeeDiscount?: number | null;
    caseFee?: number | null;
    totalPrice?: number | null;
  }
  autoCalc?: {
    newPriceWithoutEquipment?: boolean;
    equipmentPrice?: boolean;
    tradePrice?: boolean;
    registrationFee?: boolean;
    collateralFee?: boolean;
    collateralFeeB2C?: boolean;
    caseFee?: boolean;
    specialCaseFeeDiscount?: boolean;
  };
  internalNote?: string | null;
  externalNote?: string | null;
  projected?: {
    estimatedRegistrationFee?: number | null;
    calculation?: null | RegistrationFeeOutput;
  }
};

type RegulatedEquipmentMap = Record<string | "evaluationVehicle", EquipmentCatalog[]>

interface TaxFlowStoreProps {
  taxFlow: TaxFlow;
  setTaxFlow: React.Dispatch<React.SetStateAction<TaxFlow>>;
  setTaxFlowSilent: React.Dispatch<React.SetStateAction<TaxFlow>>;
  setLocks: (locks: Partial<TaxFlow["autoCalc"]>) => void;
  dirty: boolean;
  setDirty: React.Dispatch<React.SetStateAction<boolean>>;
  caseLocked: boolean;
  caseLockedLogicEnabled: boolean;
  mountedDATEquipment: EquipmentCatalog[]
  setCaseLockedOverride: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  taxScenario: TaxScenario | undefined | null;
  scenarioKey: keyof TaxScenarios;
  includedAdverts: TaxAdvert[];
  regulatedEquipmentMap: RegulatedEquipmentMap,
  equipmentSums: Record<string, number>
}

function generateInitialTaxFlow(taxCase: TaxCase, isWhiteListedVehicle: boolean): TaxFlow {
  let numberPlatesFee = 1180;
  const valueConfig = taxCase.taxCaseValueConfig
  if (valueConfig?.numberPlateFee.forced) {
    numberPlatesFee = safeNum(valueConfig.numberPlateFee.value) ?? 0
  }

  let whiteListedDiscount: number | null = 0;
  if (isWhiteListedVehicle && valueConfig?.whitelistedZeroTaxFeeDiscount?.value) {
    whiteListedDiscount = safeNum(valueConfig.whitelistedZeroTaxFeeDiscount.value)
  }

  const initTaxFlow: TaxFlow = {
    newPrice: {
      equipmentPrice: undefined,
      newPriceWithoutEquipment: undefined,
      taxableValueNewPrice: {
        newPrice: undefined,
        taxableValue: undefined,
        registrationFee: undefined,
      },
      newPrice: undefined,
      note: undefined,
      newPriceType: undefined,
      taxableValue: undefined,
      taxableValueBreakdown: {
        purchase: {
          purchasePrice: undefined,
          profitMargin: undefined,
          vatValue: undefined,
          taxableValue: undefined,
        },
        sale: {
          salesPrice: undefined,
          registrationFee: undefined,
          taxableValue: undefined,
        }
      },
      manager: {
        data: {
          selectedNewPrice: null,
          newPrices: [
            {
              amount: 0,
              author: null,
              id: "MANUAL",
              savedAt: "",
              source: "MANUAL",
              sourceId: null,
              currencyCode: "",
              dateGroup: "",
              documents: [],
              equipmentIncludedInPrice: [],
              note: null,
              prioritized: false,
              equipmentForceMounted: []
            }
          ],
          equipment: []
        },
        values: {
          equipmentPrice: null,
          newPrice: null,
        },
        meta: {
          fetchedKey: null
        }
      }
    },
    adverts: [],
    tradePrice: {
      tradePrice: undefined,
      output: null,
      regulatedEquipmentMap: null,
      summary: {
        additionalRegulation: undefined,
        baseTradePrice: undefined,
        finalTradePrice: undefined,
      },
      overrides: {
        additionalRegulation: {
          amount: undefined,
          note: undefined
        },
        mileage: undefined,
      }
    },
    registrationFee: {
      registrationFee: null,
      output: null,
    },
    projected: {
      estimatedRegistrationFee: null,
      calculation: null,
    },
    offer: {
      collateral: {
        b2cFee: undefined,
        baseFee: undefined,
        total: undefined
      },
      caseFee: undefined,
      licensePlateFee: numberPlatesFee,
      specialCaseFeeDiscount: whiteListedDiscount,
      totalPrice: undefined
    },

    autoCalc: {
      equipmentPrice: true,
      newPriceWithoutEquipment: true,
      registrationFee: true,
      tradePrice: true,
      caseFee: true,
      collateralFee: true,
      specialCaseFeeDiscount: true,
    }
  };
  return initTaxFlow
}


type InitialState = {
  taxFlow: TaxFlow | undefined | null;
  optimization: boolean;
}

const useTaxFlowStore = (initialState?: InitialState): TaxFlowStoreProps => {
  const { taxCase, isWhiteListedVehicle } = TaxCaseStore.useContainer()
  const [taxFlow, _setTaxFlow] = useState<TaxFlow>(initialState?.taxFlow ?? generateInitialTaxFlow(taxCase, isWhiteListedVehicle));
  const [dirty, setDirty] = useState(false)
  const [caseLockedOverride, setCaseLockedOverride] = useState<undefined | boolean>()
  const scenarioKey = initialState?.optimization ? "optimization" : "offer";
  const taxScenario = taxCase?.scenarios[scenarioKey];

  useEffect(() => {
    if (!initialState?.taxFlow) return;
    setTaxFlowSilent(initialState?.taxFlow);
    setDirty(false)
  }, [initialState?.taxFlow]);


  const caseLockedLogicEnabled = useMemo(() => {
    return [1, 2, 3].includes(taxCase.status?.id ?? -1)
  }, [taxCase.status])

  const caseLocked = useMemo(() => {
    if (!caseLockedLogicEnabled) return false
    if (caseLockedOverride !== undefined) return caseLockedOverride;
    return true
  }, [caseLockedOverride, caseLockedLogicEnabled])


  function setTaxFlow(action: React.SetStateAction<TaxFlow>) {
    _setTaxFlow(prev => {
      const next = typeof action === "function" ? action(prev) : action;
      if (Object.is(prev, next)) return prev;
      setDirty(true);
      return next;
    });
  }
  function setTaxFlowSilent(action: React.SetStateAction<TaxFlow>) {
    _setTaxFlow(prev => {
      const next = typeof action === "function" ? action(prev) : action;
      if (Object.is(prev, next)) return prev;
      return next;
    });
  }

  const setLocks = (locks: Partial<TaxFlow['autoCalc']>) => {
    setTaxFlow({
      ...taxFlow,
      autoCalc: {
        ...taxFlow.autoCalc,
        ...locks
      }
    })
  }


  const mountedDATEquipment = useMemo(() => {
    return taxFlow.newPrice?.manager?.data.equipment
      .filter(eq => eq.source === "DAT" && (eq.mounted || eq.isForceMounted)) ?? []
  }, [taxFlow.newPrice?.manager?.data.equipment])

  // ---------- Trade price: derivations ----------
  // These are used multiple places, so i just put them here
  const includedAdverts = useMemo(
    () => taxFlow.adverts?.filter(ad => ad.config?.isIncluded) ?? [],
    [taxFlow.adverts]
  );

  const regulatedEquipmentMap = useMemo(() => {
    const map: RegulatedEquipmentMap = {};
    const vehicleFirstReg = taxCase.vehicle?.firstRegDate;
    if (!vehicleFirstReg) return map;

    const vehicleAge = moment().diff(vehicleFirstReg, "year", true);
    map.evaluationVehicle = regulateEquipmentList(
      taxFlow.newPrice?.manager?.data.equipment ?? [],
      vehicleAge
    );

    for (const ad of taxFlow.adverts ?? []) {
      map[ad.id] = regulateEquipmentList(ad.config?.newPriceManager?.data.equipment ?? [], vehicleAge);
    }
    return map;
  }, [includedAdverts, taxCase.vehicle?.firstRegDate, taxFlow.newPrice?.manager?.data.equipment]);

  const equipmentSums = useMemo(() => {
    const sums: Record<string, number> = {};
    for (const key in regulatedEquipmentMap) {
      sums[key] = regulatedEquipmentMap[key].reduce((sum, eq) => sum + (eq.amount ?? 0), 0);
    }
    return sums;
  }, [regulatedEquipmentMap]);

  return {
    taxFlow,
    setTaxFlow,
    setTaxFlowSilent,
    setLocks,
    dirty,
    setDirty,
    caseLocked,
    caseLockedLogicEnabled,
    mountedDATEquipment,
    setCaseLockedOverride,
    taxScenario,
    scenarioKey,
    includedAdverts,
    regulatedEquipmentMap,
    equipmentSums,
  };
};

const TaxFlowStore = createContainer(useTaxFlowStore);

export default TaxFlowStore;
